import { gql, TypedDocumentNode, type ApolloCache } from "@apollo/client";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import { CRM_TASK_FIELDS_FRAGMENT } from "@/fragments";
import type { PageInfo, PaginationVariables } from "@/components/Pagination";
import { TaskStageValues, type CRMTask, type TaskStage } from "@/types";

export type TaskNode = CRMTask;

export type TasksQueryResponse = {
  tasks: {
    nodes: TaskNode[];
    pageInfo: PageInfo;
    totalCount: number;
  };
};

export type TasksInput = {
  search?: string;
  stage?: TaskStage[];
  authorId?: string;
  assignedToId?: string;
  unassigned?: boolean;
  myTasks?: boolean;
  assignedToDepartment?: string;
  authorDepartment?: string;
  teamTag?: string;
  dueDateRange?: { from?: string; to?: string };
  overdue?: boolean;
  dateRange?: { from?: string; to?: string };
  referenceableId?: string;
  referenceableType?: string[];
};

export type TaskOrderByInput = {
  field: string;
  direction: "ASC" | "DESC";
};

export type TasksQueryVariables = PaginationVariables & {
  input?: TasksInput;
  orderBy?: TaskOrderByInput;
};

export type OpenTaskCountResponse = {
  tasks: {
    totalCount: number;
  };
};

export type OpenTaskCountVariables = {
  input?: Pick<TasksInput, "assignedToId" | "teamTag" | "unassigned" | "stage">;
};

export const OPEN_TASK_COUNT_QUERY: TypedDocumentNode<OpenTaskCountResponse, OpenTaskCountVariables> = gql`
  query OpenTaskCount($input: BackofficeCRMTasksInput) {
    tasks(first: 0, input: $input) {
      totalCount
    }
  }
`;

export function openMyTaskCountVariables(assignedToId: string): OpenTaskCountVariables {
  return {
    input: {
      assignedToId,
      stage: [TaskStageValues.OPEN]
    }
  };
}

export function openTeamTaskCountVariables(teamTag: string): OpenTaskCountVariables {
  return {
    input: {
      teamTag,
      unassigned: true,
      stage: [TaskStageValues.OPEN]
    }
  };
}

export const TASKS_QUERY: TypedDocumentNode<TasksQueryResponse, TasksQueryVariables> = gql`
  query TasksQuery(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeCRMTasksInput
    $orderBy: BackofficeCRMTaskOrderByInput
  ) {
    tasks(first: $first, last: $last, before: $before, after: $after, input: $input, orderBy: $orderBy) {
      nodes {
        ...CRMTaskFields
        commentsCount
      }
      ...PageInfo
    }
  }
  ${CRM_TASK_FIELDS_FRAGMENT}
  ${PAGE_INFO_FRAGMENT}
`;

export function evictTasksCache(cache: ApolloCache<unknown>): void {
  cache.evict({ fieldName: "tasks" });
}
